import React from 'react';

import { useQuery } from '@apollo/client';
import { GET_ENRICHED_EVENT_PARTICIPANTS_BY_ID_QUERY } from 'Queries/Queries';
import { useAuth } from 'context/Auth';
import PropTypes from 'prop-types';

import { ActionButton } from 'components/ActionButton/ActionButton';
import { EventMetaData } from 'components/Event/EventMetaData/EventMetaData';
import { EventOnlineQrCode } from 'components/Event/EventOnlineQrCode/EventOnlineQrCode';
import 'components/Event/EventRegisterForm/EventRegisterForm.scss';
import { HintText } from 'components/HintText/HintText';
import { HINTTEXT_TYPE } from 'components/HintText/HintTextType';
import { LoadingIndicator } from 'components/LoadingIndicator/LoadingIndicator';
import { OuterBox } from 'components/OuterBox/OuterBox';
import { Separator } from 'components/Separator/Separator';
import { SubTitle } from 'components/SubTitle/SubTitle';

import { getEventParticipationUrl } from 'utils/UrlUtil';
import { getTenantId } from 'utils/tenantUtil';

import { ParticipantsTable } from './ParticipantsTable';

export const EventParticipantsList = ({ event }) => {
  const tenantId = getTenantId();
  const { token } = useAuth();
  const { loading, error, data } = useQuery(GET_ENRICHED_EVENT_PARTICIPANTS_BY_ID_QUERY, {
    variables: {
      eventId: event?.id,
    },
    fetchPolicy: 'network-only',
  });

  const downloadName = event.title.replace(/ /g, '_');
  const downloadParticipantsList = async () => {
    const options = {
      method: 'GET',
      headers: { Authorization: `Bearer ${token}` },
    };

    if (process.env.REACT_APP_GRAPHQL_ENDPOINT) {
      const baseURl = process.env.REACT_APP_GRAPHQL_ENDPOINT.replace('/graphql', '');
      const url = `${baseURl}/events/${event.id}/participants?tenant=${tenantId}`;

      const download = async (downloadUrl, filename) => {
        await fetch(downloadUrl, options).then((res) =>
          res.blob().then((b) => {
            const a = document.createElement('a');
            a.href = URL.createObjectURL(b);
            a.setAttribute('download', filename);
            a.click();
          })
        );
      };
      download(url, `${downloadName}.xlsx`);
    }
  };

  if (loading) return <LoadingIndicator />;

  if (error) {
    console.error('EventParticipantsList error:', error);
  }

  const allParticipants = data?.getEnrichedEventParticipants?.allParticipants;
  return error ? (
    <HintText type={HINTTEXT_TYPE.ERROR}>
      Es ist ein Fehler aufgetreten, bitte versuchen sie es später erneut.
    </HintText>
  ) : (
    <OuterBox>
      <EventMetaData event={event} />
      {event.meetingLink && <EventOnlineQrCode url={getEventParticipationUrl(event.id)} />}
      <SubTitle text="Teilnehmer" notFramed />
      <Separator />
      <ParticipantsTable participants={allParticipants} />
      <ActionButton
        inverted
        className="event-participants-list-button"
        title="Teilnehmerliste exportieren"
        onClick={downloadParticipantsList}
        disabled={allParticipants?.length === 0}
      />
    </OuterBox>
  );
};

EventParticipantsList.propTypes = {
  event: PropTypes.object.isRequired,
};
